import { ChevronLeft, ChevronRight } from "@tamagui/lucide-icons";
import { useCallback, useEffect, useState } from "react";
import { Adapt, Sheet, XStack, YStack, isWeb } from "tamagui";

import LoadingSpinner from "@/components/animated/LoadingSpinner";
import { Button } from "@/components/Button";
import CloseButton from "@/components/CloseButton";
import { Dialog } from "@/components/Dialog";
import Image from "@/components/Image";
import { useImageGalleryStore } from "@/store";

const ImageGallery = () => {
  const { imageUris, initialUri, isOpen, closeImageGallery } =
    useImageGalleryStore();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedUri, setLoadedUri] = useState<string>();

  useEffect(() => {
    if (initialUri && imageUris.length > 0) {
      const index = imageUris.findIndex((uri) => uri === initialUri);
      if (index !== -1) {
        setCurrentIndex(index);
      } else {
        setCurrentIndex(0);
      }
    } else if (!isOpen) {
      setCurrentIndex(0);
    }
  }, [initialUri, imageUris, isOpen]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((index) => Math.max(0, index - 1));
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((index) => Math.min(imageUris.length - 1, index + 1));
  }, [imageUris.length]);

  useEffect(() => {
    if (!isOpen || !isWeb) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") goToPrevious();
      else if (event.key === "ArrowRight") goToNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, goToPrevious, goToNext]);

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === imageUris.length - 1;
  const currentImageUri =
    imageUris.length > 0 ? imageUris[currentIndex] : undefined;
  const isImageLoading = loadedUri !== currentImageUri;

  return (
    <Dialog
      modal
      open={isOpen}
      onOpenChange={(open: boolean) => !open && closeImageGallery()}
      disableRemoveScroll
    >
      <Dialog.Adapt when="maxLg">
        <Sheet
          modal
          open={isOpen}
          onOpenChange={(open: boolean) => !open && closeImageGallery()}
          dismissOnOverlayPress
          disableDrag
          snapPoints={[90]}
        >
          <Sheet.Frame padding="$300" gap="$200">
            <Adapt.Contents />
          </Sheet.Frame>
          <Sheet.Overlay
            backgroundColor="$fillOverlay"
            backdropFilter="blur(8px)"
          />
        </Sheet>
      </Dialog.Adapt>
      <Dialog.Portal>
        <Dialog.Overlay
          backgroundColor="$fillOverlay"
          backdropFilter="blur(8px)"
          onPress={closeImageGallery}
        />
        <Dialog.Content
          maxWidth="$constraint"
          height="$modalHeight"
          width="100%"
        >
          <CloseButton onClose={closeImageGallery} />
          {currentImageUri && (
            <YStack flex={1} width="100%">
              <Image
                source={{ uri: currentImageUri }}
                width="100%"
                height="100%"
                objectFit="contain"
                flex={1}
                recyclingKey={currentImageUri}
                onLoadEnd={() => setLoadedUri(currentImageUri)}
              />
              {isImageLoading && (
                <YStack
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  alignItems="center"
                  justifyContent="center"
                >
                  <LoadingSpinner size="$700" />
                </YStack>
              )}
            </YStack>
          )}

          {imageUris.length > 1 && (
            <XStack alignSelf="center" gap="$150">
              <Button
                mode="tertiary"
                disabled={isFirst}
                opacity={isFirst ? 0.4 : 1}
                onPress={goToPrevious}
                icon={<ChevronLeft size={20} />}
                aria-label="Previous image"
              />
              <Button
                mode="tertiary"
                disabled={isLast}
                opacity={isLast ? 0.4 : 1}
                onPress={goToNext}
                icon={<ChevronRight size={20} />}
                aria-label="Next image"
              />
            </XStack>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

export default ImageGallery;
